/**
 * «Υλικά» του προϊόντος (καταστήματα χωρίς αποθήκη): σύνδεση με τη βιβλιοθήκη «Υλικά». «Μέσα» =
 * μπαίνει στο προϊόν, «Χωρίς» = ο ταμίας μπορεί να το βγάλει, «Έξτρα» = δική του τιμή (κενό = της
 * βιβλιοθήκης). Νέο υλικό φτιάχνεται εδώ και γράφεται με την Αποθήκευση.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, GripVertical, Plus, Search, X } from 'lucide-react';
import { ApiStoreIngredient, FormIngredient, IngRef, searchKey } from './catalogModel';
import type { NewIngredient, ProductState } from './catalogDraft';
import { cx, MoneyInput, useDismiss } from './ui';
import { DragState, dropHalf, moveItem } from './dragState';

export interface IngredientsTabProps {
  productKey: string;
  state: ProductState;
  changed: Set<string>;
  readOnly: boolean;
  library: ApiStoreIngredient[];
  newIngredients: NewIngredient[];
  drag: DragState;
  onChange: (fn: (s: ProductState) => ProductState) => void;
  onCreateIngredient: (ingredient: { name: string; price: number; category: string }) => IngRef;
}

const ProductIngredientsTab: React.FC<IngredientsTabProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { state, changed, readOnly, drag, productKey } = props;
  const rows = state.form.simple_ingredients;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState<{ name: string; price: number; category: string } | null>(null);
  const [over, setOver] = useState<{ uid: string; pos: 'before' | 'after' } | null>(null);
  const pickerRef = useDismiss(open, () => setOpen(false));
  const listId = `ings:${productKey}`;
  const notProduct = state.form.product_type !== 'ΠΡΟΙΟΝ';
  const locked = readOnly || notProduct;

  const setRows = (fn: (list: FormIngredient[]) => FormIngredient[]) =>
    props.onChange(s => ({ ...s, form: { ...s.form, simple_ingredients: fn(s.form.simple_ingredients) } }));
  const update = (uid: string, patch: Partial<FormIngredient>) => setRows(list => list.map(i => (i._uid === uid ? { ...i, ...patch } : i)));
  const mark = (uid: string, part: string) => (changed.has(`ing:${uid}:${part}`) || changed.has(`ing:${uid}`) ? 'cx-changed' : undefined);

  const linkedIds = new Set(rows.map(r => String(r.store_ingredient_id)));
  // Ίδιο φίλτρο με το παλιό παράθυρο: μόνο διαθέσιμα, χωρίς διπλά.
  const candidates = useMemo(() => {
    const q = searchKey(query.trim());
    const all = [
      ...props.library.filter(i => i.is_available).map(i => ({ ref: i.id as IngRef, name: i.name, category: i.category || '', price: Number(i.extra_price) || 0 })),
      ...props.newIngredients.map(i => ({ ref: i.ref as IngRef, name: i.name, category: i.category, price: i.price })),
    ];
    return all.filter(i => !linkedIds.has(String(i.ref)) && (!q || searchKey(i.name).includes(q) || searchKey(i.category).includes(q))).slice(0, 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- linkedIds βγαίνει από τα rows
  }, [query, props.library, props.newIngredients, rows]);

  const add = (c: { ref: IngRef; name: string; category: string; price: number }) => {
    setRows(list => [...list, {
      _uid: `i${c.ref}`, store_ingredient_id: c.ref, name: c.name, category: c.category, library_price: c.price,
      extra_price_override: null, is_default: true, is_removable: true,
    }]);
    setQuery('');
    setOpen(false);
  };

  const onDrop = (e: React.DragEvent, uid: string) => {
    e.preventDefault();
    const d = drag.current;
    const pos = over && over.uid === uid ? over.pos : dropHalf(e);
    setOver(null);
    drag.end();
    if (!d || d.kind !== 'row' || d.list !== listId || d.uid === uid) return;
    setRows(list => {
      const from = list.findIndex(i => i._uid === d.uid);
      let to = list.findIndex(i => i._uid === uid);
      if (from < 0 || to < 0) return list;
      if (pos === 'after' && to < from) to += 1;
      if (pos === 'before' && to > from) to -= 1;
      return moveItem(list, from, to);
    });
  };

  return (
    <>
      <p className="cx-hint lead">{t('catalog:ingredients.lead')}</p>
      {notProduct && <p className="cx-hint warn">{t('catalog:ingredients.onlyProductType')}</p>}
      {rows.length > 0 ? (
        <div role="table" aria-label={t('catalog:ingredients.title')}>
          <div className="cx-ig-h" role="row">
            <span /><span>{t('catalog:ingredients.name')}</span><span className="c">{t('catalog:ingredients.inside')}</span>
            <span className="c">{t('catalog:ingredients.without')}</span><span className="r">{t('catalog:ingredients.extra')}</span><span />
          </div>
          {rows.map((r, index) => {
            const drop = over && over.uid === r._uid ? over.pos : null;
            return (
              <div
                key={r._uid}
                role="row"
                className={cx('cx-ig-r', drop && `drop-${drop}`)}
                onDragOver={e => { if (drag.current?.kind === 'row' && drag.current.list === listId) { e.preventDefault(); setOver({ uid: r._uid, pos: dropHalf(e) }); } }}
                onDragLeave={() => setOver(o => (o && o.uid === r._uid ? null : o))}
                onDrop={e => onDrop(e, r._uid)}
              >
                {locked ? <span /> : (
                  <button type="button" className="cx-grip" draggable aria-label={t('catalog:ingredients.reorder', { name: r.name })} title={t('catalog:list.reorderHint')}
                    onDragStart={e => { drag.start({ kind: 'row', list: listId, uid: r._uid }); try { e.dataTransfer.setData('text/plain', r._uid); } catch { /* jsdom */ } }}
                    onDragEnd={() => { drag.end(); setOver(null); }}
                    onKeyDown={e => {
                      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                      e.preventDefault();
                      setRows(list => moveItem(list, index, index + (e.key === 'ArrowUp' ? -1 : 1)));
                    }}>
                    <GripVertical className="cx-ico" />
                  </button>
                )}
                <span className="cx-ig-n">
                  <span>{r.name}</span>
                  {r.category && <span className="cx-chip sm">{r.category}</span>}
                  {typeof r.store_ingredient_id === 'string' && <span className="cx-badge brand">{t('catalog:status.new')}</span>}
                </span>
                <span className="c">
                  <input type="checkbox" className={cx('cx-cb', mark(r._uid, 'is_default'))} checked={r.is_default} disabled={locked}
                    aria-label={t('catalog:ingredients.insideOf', { name: r.name })} onChange={e => update(r._uid, { is_default: e.target.checked })} />
                </span>
                <span className="c">
                  <input type="checkbox" className={cx('cx-cb', mark(r._uid, 'is_removable'))} checked={r.is_removable} disabled={locked}
                    aria-label={t('catalog:ingredients.withoutOf', { name: r.name })} onChange={e => update(r._uid, { is_removable: e.target.checked })} />
                </span>
                <div className="cx-sfx">
                  <MoneyInput value={r.extra_price_override} nullable disabled={locked}
                    placeholder={r.library_price.toFixed(2).replace('.', ',')}
                    title={t('catalog:ingredients.extraHint', { price: r.library_price.toFixed(2).replace('.', ',') })}
                    label={t('catalog:ingredients.extraOf', { name: r.name })}
                    changed={!!mark(r._uid, 'extra_price_override')}
                    onChange={v => update(r._uid, { extra_price_override: v })} />
                  <span>€</span>
                </div>
                {locked ? <span /> : (
                  <button type="button" className="cx-icon-btn sm" aria-label={t('catalog:ingredients.remove', { name: r.name })} title={t('catalog:ingredients.remove', { name: r.name })}
                    onClick={() => setRows(list => list.filter(i => i._uid !== r._uid))}>
                    <X className="cx-ico" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="cx-empty" style={{ padding: 20 }}>
          {props.library.length === 0 && props.newIngredients.length === 0 ? t('catalog:ingredients.libraryEmpty') : t('catalog:ingredients.none')}
        </div>
      )}

      {!locked && (creating ? (
        <div className="cx-ng" role="group" aria-label={t('catalog:ingredients.newIngredient')}>
          <div className="cx-ng-h"><span className="cx-badge brand">{t('catalog:ingredients.newIngredient')}</span><span className="cx-hint">{t('catalog:ingredients.newWhere')}</span></div>
          <div className="cx-grid3">
            <div className="cx-field" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="ni-name">{t('catalog:fields.name')}</label>
              <input id="ni-name" className="cx-inp" autoFocus value={creating.name} onChange={e => setCreating({ ...creating, name: e.target.value })} />
            </div>
            <div className="cx-field">
              <label htmlFor="ni-price">{t('catalog:ingredients.extra')}</label>
              <div className="cx-sfx">
                <MoneyInput id="ni-price" value={creating.price} label={t('catalog:ingredients.extra')} placeholder="0,00" onChange={v => setCreating({ ...creating, price: v ?? 0 })} />
                <span>€</span>
              </div>
            </div>
            {/* Κατηγορία υλικού = ελεύθερο κείμενο (store_ingredients.category), όπως στη βιβλιοθήκη. */}
            <div className="cx-field" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="ni-cat">{t('catalog:ingredients.category')} <span className="cx-opt">{t('catalog:common.optional')}</span></label>
              <input id="ni-cat" className="cx-inp" list="ni-cats" value={creating.category} onChange={e => setCreating({ ...creating, category: e.target.value })} />
              <datalist id="ni-cats">
                {Array.from(new Set(props.library.map(i => i.category).filter((c): c is string => !!c))).map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>
          <div className="cx-ng-foot">
            <span className="cx-hint">{t('catalog:ingredients.savedWith')}</span>
            <button type="button" className="cx-btn ghost sm" onClick={() => setCreating(null)}>{t('catalog:common.cancel')}</button>
            <button type="button" className="cx-btn primary sm" disabled={!creating.name.trim()}
              onClick={() => {
                const ref = props.onCreateIngredient({ name: creating.name.trim(), price: creating.price, category: creating.category.trim() });
                add({ ref, name: creating.name.trim(), category: creating.category.trim(), price: creating.price });
                setCreating(null);
              }}>
              <Check className="cx-ico" /> {t('catalog:common.add')}
            </button>
          </div>
        </div>
      ) : (
        <div className="cx-btn-row" ref={pickerRef}>
          <label className="cx-search">
            <Search className="cx-ico" aria-hidden="true" />
            <input className="cx-inp sm" value={query} placeholder={t('catalog:ingredients.addPlaceholder')} aria-label={t('catalog:ingredients.addPlaceholder')}
              onFocus={() => setOpen(true)} onChange={e => { setQuery(e.target.value); setOpen(true); }} />
          </label>
          <button type="button" className="cx-btn ghost sm" onClick={() => setCreating({ name: query.trim(), price: 0, category: '' })}>
            <Plus className="cx-ico" /> {t('catalog:ingredients.newIngredientButton')}
          </button>
          {open && (
            <div className="cx-menu" role="listbox" aria-label={t('catalog:ingredients.addPlaceholder')} style={{ top: 'calc(100% + 4px)', left: 0, right: 0, maxWidth: 'none' }}>
              {candidates.length === 0
                ? <div className="cx-menu-h">{t('catalog:ingredients.noMatches')}</div>
                : candidates.map(c => (
                  <button key={String(c.ref)} type="button" role="option" aria-selected={false} onClick={() => add(c)}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                    {c.category && <span className="cx-chip sm">{c.category}</span>}
                    <span className="cx-menu-hint">{c.price > 0 ? `+${c.price.toFixed(2).replace('.', ',')} €` : ''}</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default ProductIngredientsTab;
